import React, { useState, useEffect, useCallback } from 'react';
import Topbar from '../components/Topbar';
import { 
    getAirportSyncLogs, 
    getTop100Airports, 
    syncAirportAlerts,
    startBulkFlightSync,
    getBulkFlightSyncStatus,
    stopBulkFlightSync,
    syncAviationstackAirport,
    getAviationstackUsage
} from '../services/flightAlertService';
import { onBulkFlightSyncProgress } from '../services/socketService';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';

const DISMISSED_SYNC_KEY = 'gg_dismissed_bulk_sync_id';
const OUTCOME_COLORS = { synced: '#4ADE80', skipped: 'rgba(255,255,255,0.6)', failed: '#FF7B7B' };
const SOURCE_LABELS = { cirium: 'Cirium', aviationstack: 'Aviationstack' };

// Radio buttons to choose the flight data provider for a sync
function SourcePicker({ value, onChange }) {
    return (
        <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--dark)', marginBottom: 6 }}>Data Source</label>
            <div style={{ display: 'flex', gap: 18 }}>
                {Object.entries(SOURCE_LABELS).map(([key, label]) => (
                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--navy)', cursor: 'pointer', fontWeight: value === key ? 700 : 500 }}>
                        <input type="radio" value={key} checked={value === key} onChange={() => onChange(key)} />
                        {label}
                    </label>
                ))}
            </div>
        </div>
    );
}

export default function AirportSyncLogs() {
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState({
        todayTotalAirports: 0,
        todaySuccess: 0,
        todayFailed: 0,
        todayTotalFlights: 0
    });
    const [airports, setAirports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [resyncingCode, setResyncingCode] = useState(null);

    // Live Socket Sync State (full bulk sync state from the server, including finished airports)
    const [syncProgress, setSyncProgress] = useState(null);
    const [stoppingSync, setStoppingSync] = useState(false);
    const [budget, setBudget] = useState(null);
    const [aviationstackUsage, setAviationstackUsage] = useState(null);

    // Bulk Sync Trigger Modal State
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkDate, setBulkDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [bulkDays, setBulkDays] = useState(1);
    const [bulkLimit, setBulkLimit] = useState(0); // 0 = all
    const [bulkSource, setBulkSource] = useState('cirium');
    const [startingBulk, setStartingBulk] = useState(false);

    // Filter & Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [dateFilter, setDateFilter] = useState(() => new Date().toISOString().split('T')[0]);

    // Manual quick single airport sync modal
    const [showSingleModal, setShowSingleModal] = useState(false);
    const [singleAirport, setSingleAirport] = useState('CMH');
    const [singleDate, setSingleDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [singleDays, setSingleDays] = useState(1);
    const [singleDirection, setSingleDirection] = useState('both');
    const [singleSource, setSingleSource] = useState('cirium');
    const [singleSyncing, setSingleSyncing] = useState(false);

    // Fetch Table Logs
    const fetchLogs = useCallback(async (page = 1, limit = 10, search = '', status = 'ALL', date = '') => {
        try {
            setLoading(true);
            const res = await getAirportSyncLogs({
                page,
                limit,
                search: search.trim(),
                status: status === 'ALL' ? '' : status,
                date: date || ''
            });

            const data = res?.data || {};
            setLogs(data.logs || []);
            setTotalRecords(data.total || 0);
            setTotalPages(data.totalPages || 1);
            setCurrentPage(data.page || 1);
            if (data.stats) {
                setStats(data.stats);
            }
        } catch (err) {
            console.error('Error fetching sync logs:', err);
            toast.error('Failed to load airport sync logs');
        } finally {
            setLoading(false);
        }
    }, []);

    // Initial load: Airports + Current Sync Status Check
    useEffect(() => {
        getTop100Airports()
            .then(res => {
                const list = res?.data?.airports || res?.airports || [];
                setAirports(list);
            })
            .catch(() => {});

    }, []);

    // Restore the running (or last finished, unless dismissed) bulk sync, e.g. after the page was reopened
    const refreshBulkStatus = useCallback(async (initial = false) => {
        try {
            const res = await getBulkFlightSyncStatus();
            const data = res?.data;
            if (!data) return;
            if (data.budget) setBudget(data.budget);
            if (!data.id) return;
            let dismissedId = null;
            try { dismissedId = localStorage.getItem(DISMISSED_SYNC_KEY); } catch (e) {}
            if (data.isRunning || !initial || dismissedId !== data.id) {
                setSyncProgress(data);
            }
        } catch (e) {}
    }, []);

    useEffect(() => {
        refreshBulkStatus(true);
    }, [refreshBulkStatus]);

    // Listen to real-time WebSocket progress (each event carries the full sync state)
    useEffect(() => {
        const cleanup = onBulkFlightSyncProgress((data) => {
            setSyncProgress(data);
            if (data.isRunning) return;

            if (data.status === 'completed') {
                toast.success(data.message || 'Bulk sync completed successfully!');
            } else if (data.status === 'cancelled') {
                toast.info(data.message || 'Bulk sync was stopped.');
            } else if (data.status === 'stopped') {
                toast.warning(data.message);
            } else if (data.status === 'error') {
                toast.error(data.message);
            }
            fetchLogs(currentPage, pageSize, searchQuery, statusFilter, dateFilter);
            refreshBulkStatus();
        });

        return cleanup;
    }, [currentPage, pageSize, searchQuery, statusFilter, dateFilter, fetchLogs, refreshBulkStatus]);

    // Fallback in case socket events are missed (e.g. after a reconnect): poll while a sync is running
    const syncRunning = !!syncProgress?.isRunning;
    useEffect(() => {
        if (!syncRunning) return undefined;
        const timer = setInterval(() => refreshBulkStatus(), 15000);
        return () => clearInterval(timer);
    }, [syncRunning, refreshBulkStatus]);

    const dismissSyncProgress = () => {
        try { if (syncProgress?.id) localStorage.setItem(DISMISSED_SYNC_KEY, syncProgress.id); } catch (e) {}
        setSyncProgress(null);
    };

    useEffect(() => {
        fetchLogs(currentPage, pageSize, searchQuery, statusFilter, dateFilter);
    }, [fetchLogs, currentPage, pageSize, statusFilter, dateFilter]);

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchLogs(1, pageSize, searchQuery, statusFilter, dateFilter);
    };

    const handleResetFilters = () => {
        setSearchQuery('');
        setStatusFilter('ALL');
        setDateFilter('');
        setCurrentPage(1);
        fetchLogs(1, pageSize, '', 'ALL', '');
    };

    // Quick Re-sync for a single airport
    const handleQuickResync = async (airportCode, syncDate, source = 'cirium') => {
        try {
            setResyncingCode(airportCode);
            toast.info(`Re-syncing ${airportCode} flights for ${syncDate}...`);
            if (source === 'aviationstack') {
                // Aviationstack only serves the airport's current day
                await syncAviationstackAirport({ airport: airportCode, type: 'both' });
            } else {
                await syncAirportAlerts({
                    airport: airportCode,
                    date: syncDate,
                    type: 'both',
                    limit: 0
                });
            }
            toast.success(`${airportCode} synced successfully!`);
            fetchLogs(currentPage, pageSize, searchQuery, statusFilter, dateFilter);
        } catch (err) {
            console.error('Re-sync failed:', err);
            toast.error(`Failed to re-sync ${airportCode}: ${err.message}`);
        } finally {
            setResyncingCode(null);
        }
    };

    // Start Bulk Sync
    const handleStartBulkSync = async (e) => {
        e.preventDefault();
        try {
            setStartingBulk(true);
            const res = await startBulkFlightSync({
                date: bulkDate,
                days: Number(bulkDays),
                limit: bulkLimit > 0 ? Number(bulkLimit) : null,
                source: bulkSource
            });
            toast.success(res?.message || 'Bulk sync started!');
            setShowBulkModal(false);
            setSyncProgress({
                isRunning: true,
                status: 'started',
                current: 0,
                total: res?.data?.totalAirports || (bulkLimit > 0 ? bulkLimit : 100),
                percentage: 0,
                recent: [],
                message: 'Initializing bulk sync...'
            });
            refreshBulkStatus();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || 'Failed to start bulk sync');
        } finally {
            setStartingBulk(false);
        }
    };

    // Stop Bulk Sync
    const handleStopBulkSync = async () => {
        if (!window.confirm('Are you sure you want to cancel the bulk sync process?')) return;
        try {
            setStoppingSync(true);
            await stopBulkFlightSync();
            toast.info('Stopping sync...');
        } catch (err) {
            toast.error('Failed to stop sync: ' + err.message);
        } finally {
            setStoppingSync(false);
        }
    };

    // Single modal submit
    const handleSingleSyncSubmit = async (e) => {
        e.preventDefault();
        try {
            setSingleSyncing(true);
            if (singleSource === 'aviationstack') {
                const res = await syncAviationstackAirport({
                    airport: singleAirport,
                    type: singleDirection,
                    date: singleDate,
                    days: singleDays
                });
                if (res?.data?.errors?.length) {
                    toast.warning(`${res.message} ${res.data.errors.join('; ')}`);
                } else {
                    toast.success(res?.message || `${singleAirport} sync completed!`);
                }
                if (res?.data?.usage) setAviationstackUsage(res.data.usage);
            } else {
                await syncAirportAlerts({
                    airport: singleAirport,
                    date: singleDate,
                    days: singleDays,
                    type: singleDirection,
                    limit: 0
                });
                toast.success(`${singleAirport} sync completed!`);
            }
            setShowSingleModal(false);
            fetchLogs(currentPage, pageSize, searchQuery, statusFilter, dateFilter);
        } catch (err) {
            toast.error(`Sync failed: ${err.message}`);
        } finally {
            setSingleSyncing(false);
        }
    };

    const formatDuration = (ms) => {
        if (!ms) return '-';
        if (ms < 1000) return `${ms}ms`;
        return `${(ms / 1000).toFixed(1)}s`;
    };

    const renderPagination = () => {
        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (currentPage <= 4) {
                for (let i = 1; i <= 5; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 3) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
            } else {
                pages.push(1);
                pages.push('...');
                pages.push(currentPage - 1);
                pages.push(currentPage);
                pages.push(currentPage + 1);
                pages.push('...');
                pages.push(totalPages);
            }
        }
        return pages;
    };

    return (
        <div>
            <Topbar title="Airport Sync Logs" subtitle="Track daily flight synchronization records and trigger live multi-airport syncs" />

            <div className="content">
                {/* LIVE WEBSOCKET PROGRESS CARD */}
                {syncProgress && (
                    <div className="card" style={{
                        padding: 24,
                        marginBottom: 24,
                        background: 'linear-gradient(135deg, #0A1F44 0%, #0F2A5C 100%)',
                        color: 'white',
                        border: 'none',
                        boxShadow: '0 12px 24px -6px rgba(10, 31, 68, 0.25)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                <div style={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 12,
                                    background: 'rgba(255, 255, 255, 0.12)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2.2" strokeLinecap="round">
                                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                                    </svg>
                                </div>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif", color: 'white' }}>
                                            {syncProgress.isRunning
                                                ? `Bulk Sync In Progress: ${(syncProgress.recent || []).length} of ${syncProgress.total || 100} Airports Done`
                                                : {
                                                    completed: `Bulk Sync Completed (${syncProgress.total || 100} Airports)`,
                                                    cancelled: 'Bulk Sync Cancelled',
                                                    stopped: 'Bulk Sync Stopped (Cirium Budget)',
                                                    error: 'Bulk Sync Failed'
                                                }[syncProgress.status] || 'Bulk Sync'
                                            }
                                        </h3>
                                        <span style={{
                                            background: 'rgba(255, 203, 43, 0.2)',
                                            color: 'var(--gold)',
                                            fontWeight: 700,
                                            fontSize: 12,
                                            padding: '3px 10px',
                                            borderRadius: 20
                                        }}>
                                            {syncProgress.percentage || 0}%
                                        </span>
                                    </div>
                                    <p style={{ margin: 0, marginTop: 4, fontSize: 13, color: 'rgba(255, 255, 255, 0.75)' }}>
                                        {syncProgress.message || 'Fetching live departures and arrivals via Cirium Sky API...'}
                                    </p>
                                </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                {[
                                    { label: 'Synced', value: syncProgress.synced || 0, color: '#4ADE80' },
                                    { label: 'Skipped', value: syncProgress.skipped || 0, color: 'white' },
                                    { label: 'Failed', value: syncProgress.failed || 0, color: '#FF7B7B' },
                                    { label: 'Flights Saved', value: (syncProgress.totalFlights || 0).toLocaleString(), color: 'var(--gold)' }
                                ].map(tile => (
                                    <div key={tile.label} style={{
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        borderRadius: 10,
                                        padding: '8px 16px',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{ fontSize: 10, color: 'rgba(255, 255, 255, 0.7)', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700 }}>{tile.label}</div>
                                        <div style={{ fontSize: 18, fontWeight: 800, color: tile.color, fontFamily: "'Space Grotesk', sans-serif" }}>
                                            {tile.value}
                                        </div>
                                    </div>
                                ))}

                                {syncProgress.isRunning && (
                                    <button
                                        type="button"
                                        disabled={stoppingSync}
                                        onClick={handleStopBulkSync}
                                        className="btn btn-danger"
                                        style={{ background: 'rgba(255, 92, 92, 0.2)', color: '#FF7B7B', border: '1px solid rgba(255, 92, 92, 0.3)' }}
                                    >
                                        {stoppingSync ? 'Stopping...' : 'Stop Sync'}
                                    </button>
                                )}

                                {!syncProgress.isRunning && (
                                    <button
                                        type="button"
                                        onClick={dismissSyncProgress}
                                        className="btn btn-outline"
                                        style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none' }}
                                    >
                                        Dismiss
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* PROGRESS BAR */}
                        <div style={{ width: '100%', height: 8, background: 'rgba(255,255,255,0.15)', borderRadius: 6, overflow: 'hidden', marginBottom: 14 }}>
                            <div style={{
                                width: `${Math.min(syncProgress.percentage || 0, 100)}%`,
                                height: '100%',
                                background: syncProgress.isRunning
                                    ? 'linear-gradient(90deg, var(--blue) 0%, var(--gold) 100%)'
                                    : (syncProgress.status === 'completed' ? 'var(--green)' : '#FF7B7B'),
                                transition: 'width 0.4s ease',
                                borderRadius: 6
                            }} />
                        </div>

                        {/* STATUS PILL */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, fontSize: 12 }}>
                            {syncProgress.currentAirport && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ color: 'rgba(255,255,255,0.7)' }}>Current Airport:</span>
                                    <span style={{
                                        background: 'rgba(255, 203, 43, 0.25)',
                                        color: 'var(--gold)',
                                        fontWeight: 800,
                                        padding: '2px 8px',
                                        borderRadius: 6
                                    }}>
                                        {syncProgress.currentAirport.code}
                                    </span>
                                    <span style={{ color: 'white', fontWeight: 600 }}>{syncProgress.currentAirport.name}</span>
                                </div>
                            )}

                            <div style={{ color: 'rgba(255,255,255,0.7)' }}>
                                Source: <span style={{ color: 'white', fontWeight: 600 }}>{SOURCE_LABELS[syncProgress.source] || 'Cirium'}</span>
                                {' · '}Date: <span style={{ color: 'white', fontWeight: 600 }}>{syncProgress.date}{syncProgress.days > 1 ? ` (+${syncProgress.days - 1} day)` : ''}</span>
                                {syncProgress.startedAt && (
                                    <> · Started {new Date(syncProgress.startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>
                                )}
                            </div>
                        </div>

                        {/* FINISHED AIRPORTS (newest first, quickest airports sync first) */}
                        {(syncProgress.recent || []).length > 0 && (
                            <div style={{ marginTop: 14, maxHeight: 180, overflowY: 'auto', background: 'rgba(0,0,0,0.15)', borderRadius: 10, padding: '8px 12px', fontSize: 12 }}>
                                {syncProgress.recent.map((item, idx) => (
                                    <div key={`${item.code}-${idx}`} title={item.error || ''} style={{ display: 'flex', gap: 10, padding: '3px 0', alignItems: 'center' }}>
                                        <span style={{ color: 'rgba(255,255,255,0.5)', minWidth: 52 }}>
                                            {syncProgress.recent.length - idx}/{syncProgress.total}
                                        </span>
                                        <span style={{ color: 'var(--gold)', fontWeight: 800, minWidth: 36 }}>{item.code}</span>
                                        <span style={{ color: 'white', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                                        <span style={{ color: OUTCOME_COLORS[item.outcome], fontWeight: 700 }}>
                                            {item.outcome === 'skipped' ? 'skipped (synced recently)' : `${item.outcome} · ${item.flights} flights`}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* TOP STATS CARDS */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
                    <div className="card" style={{ padding: 20 }}>
                        <div style={{ fontSize: 11, color: 'var(--grey)', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 700 }}>
                            Airports Synced Today
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--navy)', marginTop: 6, fontFamily: "'Space Grotesk', sans-serif" }}>
                            {stats.todayTotalAirports}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--blue)', marginTop: 4, fontWeight: 600 }}>
                            Daily tracking hubs
                        </div>
                    </div>

                    <div className="card" style={{ padding: 20 }}>
                        <div style={{ fontSize: 11, color: 'var(--grey)', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 700 }}>
                            Successful Syncs
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--green)', marginTop: 6, fontFamily: "'Space Grotesk', sans-serif" }}>
                            {stats.todaySuccess}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--green)', marginTop: 4, fontWeight: 600 }}>
                            ● 100% Data received
                        </div>
                    </div>

                    <div className="card" style={{ padding: 20 }}>
                        <div style={{ fontSize: 11, color: 'var(--grey)', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 700 }}>
                            Failed / Incomplete
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 800, color: stats.todayFailed > 0 ? 'var(--red)' : 'var(--grey)', marginTop: 6, fontFamily: "'Space Grotesk', sans-serif" }}>
                            {stats.todayFailed}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--grey)', marginTop: 4, fontWeight: 600 }}>
                            Requiring attention
                        </div>
                    </div>

                    <div className="card" style={{ padding: 20 }}>
                        <div style={{ fontSize: 11, color: 'var(--grey)', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 700 }}>
                            Total Flights Stored
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 800, color: '#B8860B', marginTop: 6, fontFamily: "'Space Grotesk', sans-serif" }}>
                            {stats.todayTotalFlights.toLocaleString()}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--grey)', marginTop: 4, fontWeight: 600 }}>
                            Departures & Arrivals
                        </div>
                    </div>
                </div>

                {/* TABLE CARD */}
                <div className="card">
                    {/* TOOLBAR */}
                    <div className="toolbar">
                        <form onSubmit={handleSearchSubmit} className="search-wrap">
                            <svg viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="2" strokeLinecap="round">
                                <circle cx="11" cy="11" r="8"/>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                            </svg>
                            <input 
                                type="text"
                                className="search-input"
                                placeholder="Search by IATA code or name..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </form>

                        {/* Date Picker */}
                        <input 
                            type="date"
                            className="filter-select"
                            value={dateFilter}
                            onChange={(e) => { setDateFilter(e.target.value); setCurrentPage(1); }}
                        />

                        {/* Status Select */}
                        <select 
                            className="filter-select"
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                        >
                            <option value="ALL">All Statuses</option>
                            <option value="success">Success</option>
                            <option value="partial">Partial</option>
                            <option value="failed">Failed</option>
                        </select>

                        {(searchQuery || statusFilter !== 'ALL' || dateFilter) && (
                            <button type="button" className="btn btn-outline" onClick={handleResetFilters}>
                                Reset
                            </button>
                        )}

                        <button 
                            type="button" 
                            className="btn btn-outline"
                            onClick={() => fetchLogs(currentPage, pageSize, searchQuery, statusFilter, dateFilter)}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="23 4 23 10 17 10"/>
                                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                            </svg>
                            Refresh
                        </button>

                        <button 
                            type="button"
                            className="btn btn-sync"
                            onClick={() => {
                                setShowSingleModal(true);
                                getAviationstackUsage()
                                    .then(res => { if (res?.data) setAviationstackUsage(res.data); })
                                    .catch(() => {});
                            }}
                        >
                            Sync 1 Airport
                        </button>

                        {/* BULK SYNC PRIMARY BUTTON */}
                        <button 
                            type="button"
                            disabled={syncProgress && syncProgress.isRunning}
                            className="btn btn-primary"
                            onClick={() => {
                                setShowBulkModal(true);
                                getBulkFlightSyncStatus()
                                    .then(res => { if (res?.data?.budget) setBudget(res.data.budget); })
                                    .catch(() => {});
                                getAviationstackUsage()
                                    .then(res => { if (res?.data) setAviationstackUsage(res.data); })
                                    .catch(() => {});
                            }}
                            style={{
                                background: syncProgress && syncProgress.isRunning ? 'var(--blue)' : 'var(--navy)',
                                color: 'white'
                            }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2.5">
                                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                            </svg>
                            {syncProgress && syncProgress.isRunning 
                                ? `Syncing (${syncProgress.current}/${syncProgress.total})...`
                                : 'Bulk Sync All 100 Airports'
                            }
                        </button>
                    </div>

                    {/* TABLE */}
                    {loading && logs.length === 0 ? (
                        <div style={{ padding: 60, textAlign: 'center' }}>
                            <Spinner />
                        </div>
                    ) : logs.length === 0 ? (
                        <div style={{ padding: 60, textAlign: 'center', color: 'var(--grey)' }}>
                            <div style={{ fontSize: 32, marginBottom: 10 }}>📭</div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--dark)' }}>No sync records found</div>
                            <div style={{ fontSize: 13, marginTop: 4 }}>Run "Bulk Sync All 100 Airports" to populate today's data.</div>
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="tbl">
                                <thead>
                                    <tr>
                                        <th>Airport</th>
                                        <th>Sync Date</th>
                                        <th>Direction</th>
                                        <th>Source</th>
                                        <th>Departures</th>
                                        <th>Arrivals</th>
                                        <th>Total Flights</th>
                                        <th>Status</th>
                                        <th>Duration</th>
                                        <th>Last Updated</th>
                                        <th style={{ textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map((log) => {
                                        const isResyncing = resyncingCode === log.airport_iata;
                                        return (
                                            <tr key={log.id}>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                        <span style={{
                                                            background: '#FFFBE6',
                                                            color: '#B8860B',
                                                            fontWeight: 800,
                                                            padding: '3px 8px',
                                                            borderRadius: 6,
                                                            fontSize: 12
                                                        }}>
                                                            {log.airport_iata}
                                                        </span>
                                                        <span style={{ fontWeight: 600, color: 'var(--navy)' }}>
                                                            {log.airport_name || log.airport_iata}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td style={{ color: 'var(--dark)' }}>
                                                    {log.sync_date}
                                                </td>
                                                <td>
                                                    <span className="badge badge-admin" style={{ textTransform: 'capitalize' }}>
                                                        {log.sync_type || 'both'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="badge" style={{
                                                        background: log.data_source === 'aviationstack' ? '#F3E8FF' : '#E0F2FE',
                                                        color: log.data_source === 'aviationstack' ? '#7C3AED' : '#0369A1'
                                                    }}>
                                                        {SOURCE_LABELS[log.data_source] || 'Cirium'}
                                                    </span>
                                                </td>
                                                <td style={{ color: 'var(--blue)', fontWeight: 700 }}>
                                                    {log.departures_count || 0}
                                                </td>
                                                <td style={{ color: '#8B5CF6', fontWeight: 700 }}>
                                                    {log.arrivals_count || 0}
                                                </td>
                                                <td>
                                                    <span className="badge badge-active" style={{ fontSize: 12, padding: '4px 10px' }}>
                                                        {log.total_flights || 0}
                                                    </span>
                                                </td>
                                                <td>
                                                    {log.status === 'success' && (
                                                        <span className="badge badge-active">
                                                            ✓ Success
                                                        </span>
                                                    )}
                                                    {log.status === 'partial' && (
                                                        <span className="badge badge-soon">
                                                            ⚠️ Partial
                                                        </span>
                                                    )}
                                                    {log.status === 'failed' && (
                                                        <span className="badge badge-inactive" title={log.error_message || ''}>
                                                            ✕ Failed
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ color: 'var(--grey)' }}>
                                                    {formatDuration(log.duration_ms)}
                                                </td>
                                                <td style={{ color: 'var(--grey)', fontSize: 12 }}>
                                                    {log.updatedAt ? new Date(log.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
                                                </td>
                                                <td>
                                                    <div className="action-btns" style={{ justifyContent: 'flex-end' }}>
                                                        <button
                                                            className="action-btn"
                                                            disabled={isResyncing}
                                                            onClick={() => handleQuickResync(log.airport_iata, log.sync_date, log.data_source)}
                                                            title="Re-sync airport"
                                                        >
                                                            {isResyncing ? (
                                                                <Spinner size="sm" />
                                                            ) : (
                                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <polyline points="23 4 23 10 17 10"/>
                                                                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                                                                </svg>
                                                            )}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* PAGINATION FOOTER */}
                    {totalRecords > 0 && (
                        <div className="pagination-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderTop: '1px solid #F0F4F8' }}>
                            <div style={{ fontSize: 13, color: 'var(--grey)' }}>
                                Showing <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{((currentPage - 1) * pageSize) + 1}</span> to <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{Math.min(currentPage * pageSize, totalRecords)}</span> of <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{totalRecords}</span> airports
                            </div>

                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <button
                                    className="btn btn-outline"
                                    disabled={currentPage === 1 || loading}
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    style={{ padding: '6px 12px', fontSize: 12 }}
                                >
                                    Previous
                                </button>

                                <div style={{ display: 'flex', gap: 4 }}>
                                    {renderPagination().map((p, i) => (
                                        <button
                                            key={i}
                                            className={`btn ${currentPage === p ? 'btn-primary' : 'btn-outline'}`}
                                            onClick={() => typeof p === 'number' && setCurrentPage(p)}
                                            style={{
                                                padding: '6px 12px',
                                                fontSize: 12,
                                                minWidth: 36,
                                                cursor: typeof p === 'number' ? 'pointer' : 'default',
                                                border: typeof p === 'number' ? '' : 'none',
                                                background: typeof p === 'number' ? '' : 'transparent'
                                            }}
                                        >
                                            {p}
                                        </button>
                                    ))}
                                </div>

                                <button
                                    className="btn btn-outline"
                                    disabled={currentPage >= totalPages || loading}
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    style={{ padding: '6px 12px', fontSize: 12 }}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* BULK SYNC MODAL */}
            {showBulkModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(10, 31, 68, 0.45)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
                    <div style={{ background: 'white', borderRadius: 20, padding: 32, width: '100%', maxWidth: 480, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif", color: 'var(--navy)' }}>
                                Bulk Sync All Live Airports
                            </h3>
                            <button onClick={() => setShowBulkModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--grey)' }}>✕</button>
                        </div>

                        <form onSubmit={handleStartBulkSync} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <SourcePicker value={bulkSource} onChange={setBulkSource} />

                            <div>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--dark)', marginBottom: 6 }}>Start Date</label>
                                <input
                                    type="date"
                                    className="filter-select"
                                    style={{ width: '100%' }}
                                    value={bulkSource === 'aviationstack' ? new Date().toISOString().split('T')[0] : bulkDate}
                                    onChange={(e) => setBulkDate(e.target.value)}
                                    disabled={bulkSource === 'aviationstack'}
                                    required
                                />
                                {bulkSource === 'aviationstack' && (
                                    <div style={{ fontSize: 11, color: 'var(--grey)', marginTop: 4 }}>
                                        Aviationstack (Basic plan) only provides each airport's current day.
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--dark)', marginBottom: 6 }}>Days to Fetch</label>
                                    <select
                                        className="filter-select"
                                        style={{ width: '100%' }}
                                        value={bulkSource === 'aviationstack' ? 1 : bulkDays}
                                        onChange={(e) => setBulkDays(Number(e.target.value))}
                                        disabled={bulkSource === 'aviationstack'}
                                    >
                                        <option value={1}>Today Only (1 day)</option>
                                        <option value={2}>Today + Tomorrow (2 days)</option>
                                    </select>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--dark)', marginBottom: 6 }}>Scope</label>
                                    <select 
                                        className="filter-select"
                                        style={{ width: '100%' }}
                                        value={bulkLimit}
                                        onChange={(e) => setBulkLimit(Number(e.target.value))}
                                    >
                                        <option value={0}>All 100 Live Airports</option>
                                        <option value={10}>Top 10 Airports (Quick)</option>
                                        <option value={25}>Top 25 Airports</option>
                                        <option value={50}>Top 50 Airports</option>
                                    </select>
                                </div>
                            </div>

                            <div style={{ background: '#EEF6FF', border: '1px solid #B3D9FF', borderRadius: 10, padding: 12, fontSize: 12, color: 'var(--navy)', lineHeight: 1.6 }}>
                                ℹ️ Only flight data is synced (no alert rules). Airports with the fewest flights sync first,
                                and live progress shows here even if you leave and reopen this page.
                                {(() => {
                                    const airportCount = bulkLimit > 0 ? bulkLimit : (airports.length || 100);
                                    if (bulkSource === 'aviationstack') {
                                        const calls = Math.ceil(airportCount * 2 * 3.5);
                                        const left = aviationstackUsage?.left;
                                        const short = left !== undefined && calls > left;
                                        return (
                                            <div style={{ marginTop: 6, fontWeight: 600, color: short ? 'var(--red)' : 'var(--navy)' }}>
                                                Needs about {calls.toLocaleString()} Aviationstack calls (1 per 100 flights; small airports 2, hubs up to 20).
                                                {left !== undefined && ` ${left.toLocaleString()} of ${aviationstackUsage.limit.toLocaleString()} left this billing period.`}
                                                {' '}Requests are paced for the plan's rate limit, so a full run takes a while.
                                            </div>
                                        );
                                    }
                                    const calls = airportCount * Number(bulkDays) * 2 * 4;
                                    const short = budget && calls > budget.leftToday;
                                    return (
                                        <div style={{ marginTop: 6, fontWeight: 600, color: short ? 'var(--red)' : 'var(--navy)' }}>
                                            Needs up to {calls.toLocaleString()} Cirium calls (8 per airport per day).
                                            {budget && ` ${budget.leftToday.toLocaleString()} calls left today.`}
                                            {short && ' The sync will stop when today\'s budget runs out; airports synced in the last few hours are skipped and cost nothing.'}
                                        </div>
                                    );
                                })()}
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
                                <button type="button" className="btn btn-outline" onClick={() => setShowBulkModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" disabled={startingBulk} className="btn btn-primary">
                                    {startingBulk ? 'Starting...' : 'Confirm & Start Sync'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* SINGLE AIRPORT MODAL */}
            {showSingleModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(10, 31, 68, 0.45)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
                    <div style={{ background: 'white', borderRadius: 20, padding: 32, width: '100%', maxWidth: 460, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif", color: 'var(--navy)' }}>
                                Sync Single Airport
                            </h3>
                            <button onClick={() => setShowSingleModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--grey)' }}>✕</button>
                        </div>

                        <form onSubmit={handleSingleSyncSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <SourcePicker value={singleSource} onChange={setSingleSource} />

                            <div>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--dark)', marginBottom: 6 }}>Airport</label>
                                <select 
                                    className="filter-select"
                                    style={{ width: '100%' }}
                                    value={singleAirport}
                                    onChange={(e) => setSingleAirport(e.target.value)}
                                >
                                    {airports.map(apt => (
                                        <option key={apt.id || apt.code} value={apt.code}>
                                            #{apt.rank || '-'} {apt.code} - {apt.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--dark)', marginBottom: 6 }}>Flight Date</label>
                                    <input 
                                        type="date"
                                        className="filter-select"
                                        style={{ width: '100%' }}
                                        value={singleDate}
                                        onChange={(e) => setSingleDate(e.target.value)}
                                        required
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--dark)', marginBottom: 6 }}>Days to Fetch</label>
                                    <select
                                        className="filter-select"
                                        style={{ width: '100%' }}
                                        value={singleDays}
                                        onChange={(e) => setSingleDays(Number(e.target.value))}
                                    >
                                        <option value={1}>1 Day (Selected date only)</option>
                                        <option value={2}>2 Days (Selected + Next day)</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--dark)', marginBottom: 6 }}>Direction</label>
                                <select 
                                    className="filter-select"
                                    style={{ width: '100%' }}
                                    value={singleDirection}
                                    onChange={(e) => setSingleDirection(e.target.value)}
                                >
                                    <option value="both">Both (Departures + Arrivals)</option>
                                    <option value="departure">Departures Only</option>
                                    <option value="arrival">Arrivals Only</option>
                                </select>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
                                <button type="button" className="btn btn-outline" onClick={() => setShowSingleModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" disabled={singleSyncing} className="btn btn-primary">
                                    {singleSyncing ? 'Syncing...' : 'Start Sync'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
